import { Fragment, useContext, useEffect } from "react";

import { useSubscriptionQuery } from "~/api/subscription";
import { ErrorLoadingText } from "~/components/_deprecated/errors/error-loading-text";
import { LoadingText } from "~/components/_deprecated/loading/loading-text";
import Flex from "~/components/_deprecated/primitives/flex";
import Text from "~/components/_deprecated/primitives/text";
import { AccountContext } from "~/components/account-context/account.context";
import { useToast } from "~/components/alerts/toast-provider";
import { theme } from "~/components/theme";
import { useI18n } from "~/hooks/use-i18n";

import { Button, Checkbox } from "../../../components/_deprecated/forms";
import { Box } from "../../../components/_deprecated/primitives";
import { Addon } from "./use-addons";

export const PaymentInformationWithAddons = ({
  packageId,
  packageName,
  addons,
  addonsLoading,
  addonsError,
  refetchAddons,
  toggleAddon,
}: {
  packageId: string;
  packageName: string;
  addons: Addon[];
  addonsLoading: boolean;
  addonsError: boolean;
  toggleAddon: (id: number) => void;
  refetchAddons: () => void;
}) => {
  const gstRate = 15;
  const { t, formatCurrency } = useI18n("admin-org");
  const { organisation } = useContext(AccountContext);
  const { enqueueError } = useToast();

  const {
    data,
    isLoading: loading,
    isError,
  } = useSubscriptionQuery({
    estimateInput: {
      packageId,
      addOnIds: addons.filter(a => a.enabled).map(a => a.id),
    },
  });

  // TODO: Can we display an error within the UI instead of a toast via useEffect?
  useEffect(() => {
    if (isError) {
      enqueueError(t("error_loading", { component: t("subscription_details") }));
    }
  }, [isError]);

  const packageInformation = data?.packageChangeEstimate;

  const border = `1px solid ${theme.colors.neutral.medium}`;

  return (
    <Box data-testid="payment-information-with-addons">
      <Flex borderTop={border} borderBottom={border} py="2">
        <div className="basis-[70%]">
          {t("admin-org:subscriptions.plan.plan_users", { packageName })}
        </div>
        {packageInformation ? (
          <>
            <div className="basis-[20%]">
              {t("admin-org:subscriptions.plan.user_cost", {
                users: packageInformation.totalBillableUsers,
                monthlyCost: formatCurrency(
                  packageInformation.planPerUnitCosts / 100,
                  packageInformation.currencyCode ?? undefined
                ),
              })}
            </div>
            <div className="basis-[10%] text-right">
              <Text fontWeight="bold">
                {formatCurrency(
                  (packageInformation.totalBillableUsers * packageInformation.planPerUnitCosts) /
                    100,
                  packageInformation.currencyCode
                )}
              </Text>
            </div>
          </>
        ) : (
          <Fragment>
            <div className="basis-[20%]" />
            <div className="basis-[10%] text-right">
              <Text data-testid="package-cost-calculating">
                {t("admin-org:subscriptions.calculating")}
              </Text>
            </div>
          </Fragment>
        )}
      </Flex>
      <Flex py="2">
        <Text fontWeight="bold" fontSize="3">
          {t("admin-org:subscriptions.plan.addons_title")}
        </Text>
      </Flex>
      <Box minHeight="10rem" borderBottom={border}>
        {addonsLoading ? (
          <LoadingText text={t("admin-org:subscriptions.plan.addons_loading")} />
        ) : addonsError ? (
          <Box>
            <ErrorLoadingText text={t("admin-org:subscriptions.plan.addons_loading_error")} />
            <Button mt="2" onClick={() => refetchAddons()}>
              {t("admin-org:subscriptions.plan.addons_refetch_button")}
            </Button>
          </Box>
        ) : addons.length === 0 ? (
          <Text>{t("admin-org:subscriptions.plan.addons_empty")}</Text>
        ) : addons.length !== 0 ? (
          <Fragment>
            {addons.map(addonFeature => (
              <Flex key={addonFeature.id} data-testid={`addon-row-${addonFeature.id}`}>
                <div className="basis-[70%]">
                  <Flex width="100%">
                    <Checkbox
                      data-testid={`addon_${addonFeature.id}`}
                      name={`addon_${addonFeature.id}`}
                      id={`addon_${addonFeature.id}`}
                      checked={Boolean(addonFeature.enabled)}
                      onChange={() => toggleAddon(addonFeature.id)}
                    />
                    <Box ml="4">
                      <Text fontWeight="bold">{addonFeature.name}</Text>
                      <Text>{addonFeature.description}</Text>
                    </Box>
                  </Flex>
                </div>
                <div className="basis-[20%]">
                  {t("admin-org:subscriptions.plan.addon_cost", {
                    users: addonFeature.billableUsers,
                    monthlyCost: formatCurrency(
                      addonFeature.unitPrice / 100,
                      packageInformation?.currencyCode
                    ),
                  })}
                </div>
                <div className="basis-[10%] text-right">
                  <Text fontWeight="bold" data-testid={`addon-${addonFeature.id}-total`}>
                    {formatCurrency(
                      addonFeature.totalPrice / 100,
                      packageInformation?.currencyCode
                    )}
                  </Text>
                </div>
              </Flex>
            ))}
          </Fragment>
        ) : null}
      </Box>
      {organisation.countryCode === "NZ" && (
        <Flex py="2" data-testid="gst-row">
          <div className="basis-[70%]" />
          <div className="basis-[20%]">
            {t("admin-org:subscriptions.plan.gst_label", { gstRate })}
          </div>
          <div className="basis-[10%] text-right">
            {loading ? (
              <Text data-testid="package-gst-calculating">
                {t("admin-org:subscriptions.calculating")}
              </Text>
            ) : packageInformation ? (
              <Text fontWeight="bold">
                {formatCurrency(
                  (packageInformation.monthlyCost / 10000) * gstRate,
                  packageInformation.currencyCode
                )}
              </Text>
            ) : null}
          </div>
        </Flex>
      )}
      <Flex py="1">
        <div className="basis-[70%]" />
        <div className="basis-[20%]">{t("admin-org:subscriptions.plan.monthly_total_label")}</div>
        <div className="basis-[10%] text-right">
          {loading ? (
            <Text data-testid="package-total-calculating">
              {t("admin-org:subscriptions.calculating")}
            </Text>
          ) : packageInformation ? (
            <Text fontWeight="bold" data-testid="package-total">
              {formatCurrency(
                packageInformation.monthlyCost / 100 +
                  (organisation.countryCode === "NZ"
                    ? (packageInformation.monthlyCost / 10000) * gstRate
                    : 0),
                packageInformation.currencyCode
              )}
            </Text>
          ) : null}
        </div>
      </Flex>
    </Box>
  );
};
